/**
 * App — root component. Mounts the router and the toast portal.
 *
 * On boot:
 *   - Re-applies the persisted theme to <html data-theme="…">
 *   - Asks the API who's logged in (so the avatar/sidebar reflect server truth)
 */
import { useEffect } from "react";
import { RouterProvider } from "react-router-dom";
import { Toaster } from "sonner";
import { MotionConfig, LazyMotion, domAnimation } from "framer-motion";
import { router } from "@/router";
import { AuthSessionWatcher } from "@/components/app/auth-session-watcher";
import { SessionExpiredDialog } from "@/components/app/session-expired-dialog";
import { useTheme } from "@/stores/theme";
import { useAuth } from "@/stores/auth";
import { useRunNotifierWatcher } from "@/stores/run-notifier";
import { getLogger } from "@/lib/logging";

const logger = getLogger("opus.app");

export function App() {
  const rehydrate = useTheme((s) => s.rehydrate);
  const bootstrap = useAuth((s) => s.bootstrap);
  const resolved = useTheme((s) => s.resolved);
  // Kick the workflow-completion watcher — it polls /workflows/runs/ and
  // fires desktop+sound notifications on terminal-state transitions.
  useRunNotifierWatcher();

  useEffect(() => {
    logger.debug("app_bootstrap_start");
    // Apply persisted theme + resolve "system" against the OS preference.
    rehydrate();
    logger.debug("app_theme_rehydrated");
    // Fire-and-forget — UI shows logged-out state until this resolves.
    void bootstrap()
      .then(() => {
        logger.info("app_bootstrap_complete");
      })
      .catch((error) => {
        logger.error({ error: String(error) }, "app_bootstrap_error");
      });
  }, [rehydrate, bootstrap]);

  return (
    <LazyMotion features={domAnimation}>
      <MotionConfig reducedMotion="user">
        <AuthSessionWatcher />
        <RouterProvider router={router} />
        <SessionExpiredDialog />
        <Toaster
          position="bottom-right"
          theme={resolved}
          toastOptions={{
            // Match Aurora chrome: rounded, soft-shadow, surface bg.
            className:
              "rounded-xl border border-line bg-surface text-fg shadow-[0_18px_40px_-22px_rgba(125,108,255,0.45)]",
          }}
        />
      </MotionConfig>
    </LazyMotion>
  );
}
